import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { DotsVertical, InfoCircle, Pencil, Trash } from '@saleshandy/icons';
import { formatNoteTimestamp } from '../../../../../../../shared/utils';
import {
  GetSignedUrlForVoicemailTemplateResponse,
  Template,
} from '../../../../../types';
import AudioPlayer from '../../../../audio-player';
import { RootState } from '../../../../../../../store/root-reducer';
import SentEmailIcon from '../../../../../../../shared/components/images/sent-email-icon';
import { CallBackIcon } from '../../../../../../../shared/svg/call-back';
import Avatar from '../../../../../../../shared/design-system/components/atoms/initials-avatar';
import { RequestState } from '../../../../../../../shared/types/request-state';
import { getSignedUrlForVoicemailTemplateRequest } from '../../../../../extra-actions';
import TranscriptCell from '../../call-logs-table/cells/transcript-cell';
import { formatNumberToK } from '../../../../../../home/components/header/header-helper';
import TruncatedTextWithAutoTooltip from '../../../../../../../shared/components/truncated-text-with-auto-tooltip';
import hasPermission from '../../../../../../../shared/utils/access-control/has-permission';
import { Permissions } from '../../../../../../../shared/utils/access-control/enums/permissions';
import { OverlayTooltip } from '../../../../../../../shared/design-system/components/overlay/tooltip';

const columnHelper = createColumnHelper<Template>();

const VoicemailTemplateDateCell: React.FC<{
  dateCreated: string | null | undefined;
}> = memo(({ dateCreated }) => {
  const userTimezone = useSelector((state: RootState) => state?.home?.timeZone);
  if (!dateCreated) {
    return <span>-</span>;
  }
  const displayText = formatNoteTimestamp(
    dateCreated,
    false,
    userTimezone,
    true,
  );
  const tooltipText = formatNoteTimestamp(dateCreated, true, userTimezone);

  return (
    <span className="d-flex align-items-center">
      <TruncatedTextWithAutoTooltip
        text={displayText}
        tooltipText={tooltipText}
      />
    </span>
  );
});

VoicemailTemplateDateCell.displayName = 'VoicemailTemplateDateCell';

export interface VoicemailTemplateActionsCallbacks {
  onEdit?: (template: Template) => void;
  onDelete?: (template: Template) => void;
}

/**
 * Actions menu for voicemail template row.
 * Uses plain button + createPortal so the menu renders in document.body
 * and is not clipped by table overflow. Bypasses React-Bootstrap Dropdown
 * to avoid trigger issues in table cells.
 */
const VoicemailTemplateActionsCell: React.FC<{
  row: Template;
  onEdit?: (template: Template) => void;
  onDelete?: (template: Template) => void;
}> = memo(({ row, onEdit, onDelete }) => {
  const canUpdate = hasPermission(Permissions.VOICEMAIL_TEMPLATE_UPDATE);
  const canDelete = hasPermission(Permissions.VOICEMAIL_TEMPLATE_DELETE);
  const [show, setShow] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);

  // Hide actions menu entirely when user has no action permissions
  if (!canUpdate && !canDelete) {
    return null;
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        toggleRef.current &&
        !toggleRef.current.contains(event.target as Node) &&
        !(event.target as Element)?.closest(
          '.voicemail-template-actions-dropdown',
        )
      ) {
        setShow(false);
      }
    };
    if (show) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [show]);

  // Compute menu position - use useLayoutEffect so it's ready before paint
  const menuStyle: React.CSSProperties = (() => {
    if (!show || !toggleRef.current) return {};
    const rect = toggleRef.current.getBoundingClientRect();
    return {
      position: 'fixed' as const,
      top: rect.bottom + 4,
      left: rect.right - 180, // Align right edge of menu with right edge of button (menu ~180px)
      zIndex: 1050,
    };
  })();

  const handleToggleClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShow((prev) => !prev);
  }, []);

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div
      className="voicemail-template-actions-cell d-flex justify-content-center"
      style={{ position: 'relative', zIndex: 1 }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <button
        ref={toggleRef}
        type="button"
        className={`voicemail-template-actions-toggle p-0 border-0 d-flex align-items-center justify-content-center ${
          show ? 'active' : ''
        }`}
        aria-label="Actions"
        aria-haspopup="true"
        aria-expanded={show}
        onClick={handleToggleClick}
      >
        <DotsVertical width={16} height={16} color="#6B7280" />
      </button>
      {typeof document !== 'undefined' &&
        show &&
        createPortal(
          <div
            className="dropdown-menu-custom voicemail-template-actions-dropdown show"
            role="menu"
            style={menuStyle}
          >
            {canUpdate && (
              <button
                type="button"
                role="menuitem"
                className="dropdown-item-custom"
                onClick={() => {
                  setShow(false);
                  onEdit?.(row);
                }}
              >
                <Pencil width={16} height={16} />
                Edit Voicemail
              </button>
            )}
            {canUpdate && canDelete && (
              <hr className="dropdown-divider-custom" />
            )}
            {canDelete && (
              <button
                type="button"
                role="menuitem"
                className="dropdown-item-custom "
                onClick={() => {
                  setShow(false);
                  onDelete?.(row);
                }}
              >
                <Trash width={16} height={16} />
                Delete Voicemail
              </button>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
});

VoicemailTemplateActionsCell.displayName = 'VoicemailTemplateActionsCell';

const VoicemailInlineAudioCell: React.FC<{ row: Template }> = ({ row }) => {
  const dispatch = useDispatch();

  // Need to preserve the voicemail Url in tab switch until user reloads the page(Use this)
  const signedUrl = useSelector(
    (state: RootState) =>
      state?.dialer?.getSignedUrlForVoicemailTemplateResponses?.[row?.id]
        ?.signedUrl ?? null,
  );
  // IF no need to preserve the voicemail Url in tab switch until (Use this)

  // const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [shouldAutoPlay, setShouldAutoPlay] = useState(false);

  const handlePlayWithoutSrc = useCallback(async () => {
    const templateId = Number(row.id);
    if (!Number.isFinite(templateId)) return;

    setShouldAutoPlay(true);
    setIsLoading(true);
    try {
      const action: any = await dispatch(
        getSignedUrlForVoicemailTemplateRequest(templateId) as any,
      );
      const payload = action?.payload?.payload ?? action?.payload;
      if (!payload?.signedUrl) {
        setIsLoading(false);
        // setSignedUrl(payload.signedUrl as string);
      }
    } catch {
      setIsLoading(false);
    }
  }, [dispatch, row.id]);

  const audioSrc = signedUrl ?? row?.voicemailAudioUrl;

  return (
    <AudioPlayer
      pauseOnScroll={false}
      defaultDuration={row?.duration}
      audioSrc={audioSrc}
      className={`voicemail-greeting-audio-player ${
        isLoading ? 'audio-player-loading' : ''
      }`}
      showPlaybackSpinner={isLoading}
      onPlayWithoutSrc={handlePlayWithoutSrc}
      autoPlayWhenSrcAvailable={shouldAutoPlay}
      onAutoPlayCompleted={() => {
        setShouldAutoPlay(false);
        setIsLoading(false);
      }}
    />
  );
};

VoicemailInlineAudioCell.displayName = 'VoicemailInlineAudioCell';

export interface VoicemailTemplatesColumnDefinitionCallbacks {
  onEditTemplate?: (template: Template) => void;
  onDeleteTemplate?: (template: Template) => void;
  getSignedUrlForVoicemailTemplateResponses?: Record<
    number,
    GetSignedUrlForVoicemailTemplateResponse
  >;
  getSignedUrlForVoicemailTemplateRequest?: (templateId: number) => void;
  getSignedUrlForVoicemailTemplateRequestStatus?: RequestState;
  getSignedUrlForVoicemailTemplatePendingId?: number | null;
  /** Called when user clicks play with no audio - parent tracks this to survive table re-renders */
  onPlayRequested?: (templateId: number) => void;
  /** Called after auto-play completes - clears the play-requested state */
  clearPlayRequested?: (templateId: number) => void;
  /** Whether the template was requested to play (parent-controlled, survives remount) */
  isPlayRequestedForTemplate?: (templateId: number) => boolean;
  resetGetSignedUrlForVoicemailTemplateRequest?: () => void;
  setSelectedTemplateId?: (id: number | null) => void;
  selectedTemplateId?: number | null;
}
const performanceLabel = (
  <div className="d-flex align-items-center gap-4">
    <span>Performance</span>
    <OverlayTooltip text="Voicemails dropped and callbacks received from those prospects">
      <InfoCircle width={14} height={14} color="#6b7280" cursor="pointer" />
    </OverlayTooltip>
  </div>
);

export const getVoicemailTemplatesColumnDefinitions = (
  callbacks?: VoicemailTemplatesColumnDefinitionCallbacks,
): ColumnDef<Template, any>[] => {
  const {
    onEditTemplate,
    onDeleteTemplate,
    setSelectedTemplateId,
    selectedTemplateId,
  } = callbacks ?? {};
  const titleColumn = columnHelper.accessor('title', {
    id: 'title',
    header: 'Title',
    cell: ({ getValue }) => (
      <span className="d-flex align-items-center">
        <TruncatedTextWithAutoTooltip text={getValue() || '-'} />
      </span>
    ),
    size: 220,
    minSize: 120,
    maxSize: 340,
    enableSorting: true,
  });

  const voicemailColumn = columnHelper.accessor(
    (row) => row.voicemailAudioUrl ?? null,
    {
      id: 'voicemail',
      header: 'Voicemail',
      cell: ({ row }) => <VoicemailInlineAudioCell row={row.original} />,
      size: 220,
      minSize: 80,
      maxSize: 280,
      enableSorting: false,
    },
  );

  const transcriptColumn = columnHelper.accessor('transcription', {
    id: 'transcript',
    header: 'Transcript',
    cell: ({ getValue, row }) => {
      const { original } = row;
      const value = getValue() as string | null;
      return (
        <TranscriptCell
          transcript={value}
          original={original}
          setSelectedTemplateId={setSelectedTemplateId}
          selectedTemplateId={selectedTemplateId}
        />
      );
    },
    size: 260,
    minSize: 160,
    maxSize: 400,
    enableSorting: false,
  });

  const performanceColumn = columnHelper.accessor('performance', {
    id: 'performance',
    header: () => performanceLabel,
    cell: ({ row }) => (
      <div className="performance-wrapper">
        <div className="performance-item">
          <SentEmailIcon />
          <span className="performance-item-text">
            {formatNumberToK(row?.original?.totalSent) ?? '-'}
          </span>
        </div>
        <div className="performance-item">
          <CallBackIcon width={14} height={14} />
          <span className="performance-item-text">
            {formatNumberToK(row?.original?.callbacksReceived) ?? '-'}
          </span>
        </div>
      </div>
    ),
    size: 140,
    minSize: 100,
    maxSize: 200,
    enableSorting: false,
  });

  const dateCreatedColumn = columnHelper.accessor(
    (row) => row.dateCreated ?? row.createdAt ?? null,
    {
      id: 'dateCreated',
      header: 'Date Created',
      cell: ({ row }) => (
        <VoicemailTemplateDateCell
          dateCreated={row.original.dateCreated ?? row.original.createdAt}
        />
      ),
      size: 180,
      minSize: 120,
      maxSize: 260,
      enableSorting: true,
    },
  );

  const createdByColumn = columnHelper.accessor(
    (row) => {
      const u = row.createdByUser;
      return u ? `${u.firstName} ${u.lastName}`.trim() || '-' : '-';
    },
    {
      id: 'createdBy',
      header: 'Created By',
      cell: ({ row }) => {
        const user = row?.original?.createdByUser ?? row?.original?.user;
        const firstName = user?.firstName ?? '';
        const lastName = user?.lastName ?? '';
        return (
          <div className="created-by-wrapper">
            <Avatar
              firstName={firstName}
              lastName={lastName}
              className="created-by-avatar"
            />
            <span className="created-by-text d-flex align-items-center">
              <TruncatedTextWithAutoTooltip text={`${firstName} ${lastName}`} />
            </span>
          </div>
        );
      },
      size: 180,
      minSize: 100,
      maxSize: 260,
      enableSorting: false,
    },
  );

  const actionsColumn = columnHelper.display({
    id: 'actions',
    header: '',
    size: 64,
    minSize: 64,
    maxSize: 64,
    enablePinning: true,
    enableResizing: false,
    cell: ({ row }) => (
      <VoicemailTemplateActionsCell
        row={row.original}
        onEdit={onEditTemplate}
        onDelete={onDeleteTemplate}
      />
    ),
  });

  return [
    titleColumn,
    voicemailColumn,
    transcriptColumn,
    performanceColumn,
    dateCreatedColumn,
    createdByColumn,
    actionsColumn,
  ];
};
